import { z } from "zod";
import type { HardStop } from "./common";

// TES-1250 (Ella 2.1 slice D) — the shared symptom structure: one SymptomEntry
// per symptom she follows, reused by F-04, F-05, direct registration under
// "Din hälsa" and, once introduced, "Besvärsguiden" (Emelie, Ö-450:
// "Samma SymptomEntry ska återanvändas mellan F-04, F-05, dosregistrering och Besvärsguiden.").
// TES-1438: not by the dose registration any more, see
// DIRECT_SYMPTOM_SURFACES.
//
// The questions and scales are Drive's "Ella 2.1 — Productdefinition,
// frågeregister, Besvärsguide och datakongruens" §5.4, verbatim. The
// canonical question register (question-register.v1.ts, TES-1247) reads
// these questions and the symptom list from here rather than repeating them,
// so this file stays their one copy; the ids are the register's.

type Copy = { sv: string; en: string };
interface Option<V extends string = string> {
  value: V;
  label: Copy;
}

// ---------------------------------------------------------------------------
// The symptom register, version 2: Drive §6.1 "Första kanoniska besvärslista".
//
// Codes are stable identifiers stored on every entry. v1's codes are kept
// where the symptom is the same one renamed (abdominal_pain was "Magont",
// reflux "Halsbränna eller reflux", dizziness "Yrsel eller
// svimningskänsla", other "Annat"); intake_difficulty is new in v2.
// symptom-register.v1.ts in the api stays as it is for the forms that use it.

export const SYMPTOM_REGISTER_VERSION = 2;

export const SYMPTOM_REGISTER_V2 = [
  { code: "nausea", label: { sv: "Illamående", en: "Nausea" } },
  { code: "vomiting", label: { sv: "Kräkningar", en: "Vomiting" } },
  { code: "constipation", label: { sv: "Förstoppning", en: "Constipation" } },
  { code: "diarrhea", label: { sv: "Diarré", en: "Diarrhoea" } },
  { code: "abdominal_pain", label: { sv: "Buksmärta", en: "Abdominal pain" } },
  {
    code: "reflux",
    label: {
      sv: "Halsbränna och sura uppstötningar",
      en: "Heartburn and acid reflux",
    },
  },
  { code: "dizziness", label: { sv: "Yrsel", en: "Dizziness" } },
  { code: "headache", label: { sv: "Huvudvärk", en: "Headache" } },
  { code: "fatigue", label: { sv: "Trötthet", en: "Fatigue" } },
  {
    code: "injection_site",
    label: {
      sv: "Reaktion vid injektionsstället",
      en: "Injection-site reaction",
    },
  },
  {
    code: "intake_difficulty",
    label: {
      sv: "Svårt att få i sig tillräckligt med mat eller vätska",
      en: "Difficulty getting enough food or fluids",
    },
  },
  { code: "other", label: { sv: "Annat besvär", en: "Other symptom" } },
] as const satisfies readonly { code: string; label: Copy }[];

export type SymptomCode = (typeof SYMPTOM_REGISTER_V2)[number]["code"];
export const SYMPTOM_CODES = SYMPTOM_REGISTER_V2.map((s) => s.code) as [
  SymptomCode,
  ...SymptomCode[],
];

export function symptomLabel(code: string): Copy | null {
  return SYMPTOM_REGISTER_V2.find((s) => s.code === code)?.label ?? null;
}

// ---------------------------------------------------------------------------
// The symptom questions (Drive §5.4).

export const SYMPTOM_SEVERITY_VALUES = [
  "latt",
  "mattligt",
  "kraftigt",
] as const;
export type SymptomSeverity = (typeof SYMPTOM_SEVERITY_VALUES)[number];

export const SYMPTOM_TREND_VALUES = [
  "nytt",
  "battre",
  "oforandrat",
  "varre",
  "kommit_tillbaka",
] as const;
export type SymptomTrend = (typeof SYMPTOM_TREND_VALUES)[number];

export const SYMPTOM_INTAKE_VALUES = [
  "ja",
  "oftast",
  "ibland_svart",
  "ofta_svart",
] as const;
export type SymptomIntake = (typeof SYMPTOM_INTAKE_VALUES)[number];

export const SYMPTOM_QUESTIONS = {
  SYMPTOM_TYPE: {
    label: {
      sv: "Vilket besvär vill du registrera?",
      en: "Which symptom do you want to register?",
    },
  },
  SYMPTOM_SEVERITY: {
    label: {
      sv: "Hur mycket besvär har du av detta just nu?",
      en: "How much does this bother you right now?",
    },
    options: [
      { value: "latt", label: { sv: "Lätt", en: "Mild" } },
      { value: "mattligt", label: { sv: "Måttligt", en: "Moderate" } },
      { value: "kraftigt", label: { sv: "Kraftigt", en: "Severe" } },
    ] satisfies Option<SymptomSeverity>[],
  },
  SYMPTOM_TREND: {
    label: {
      sv: "Hur har besväret förändrats?",
      en: "How has the symptom changed?",
    },
    options: [
      { value: "nytt", label: { sv: "Nytt", en: "New" } },
      { value: "battre", label: { sv: "Bättre", en: "Better" } },
      { value: "oforandrat", label: { sv: "Oförändrat", en: "Unchanged" } },
      { value: "varre", label: { sv: "Värre", en: "Worse" } },
      {
        value: "kommit_tillbaka",
        label: { sv: "Kommit tillbaka", en: "Come back" },
      },
    ] satisfies Option<SymptomTrend>[],
  },
  SYMPTOM_STARTED_AT: {
    label: {
      sv: "När började besväret eller när förändrades det?",
      en: "When did the symptom start, or when did it change?",
    },
  },
  SYMPTOM_INTAKE: {
    label: {
      sv: "Har du kunnat äta och dricka tillräckligt?",
      en: "Have you been able to eat and drink enough?",
    },
    options: [
      { value: "ja", label: { sv: "Ja", en: "Yes" } },
      { value: "oftast", label: { sv: "Oftast", en: "Mostly" } },
      {
        value: "ibland_svart",
        label: { sv: "Ibland svårt", en: "Sometimes difficult" },
      },
      {
        value: "ofta_svart",
        label: { sv: "Ofta svårt", en: "Often difficult" },
      },
    ] satisfies Option<SymptomIntake>[],
  },
  SYMPTOM_NOTE: {
    label: {
      sv: "Är det något mer du vill beskriva?",
      en: "Is there anything else you want to describe?",
    },
  },
} as const;

// "Borta" (Ö-452): her own end state for an entry. Not a trend value — it
// closes the entry instead of describing it — and never set by the system.
export const SYMPTOM_RESOLVED_LABEL: Copy = { sv: "Borta", en: "Gone" };

// TES-1511 — the patient's words for a report about her earlier treatment
// (referencePeriod before_ella) and for a history entry. Emelie 2026-10-19
// (TES-1437): "Historiska/avslutade besvär, aktuell intensitet och
// månadsperiodens sammanfattning hålls isär." Ö-792 (Emelie 2026-10-21):
// "Rubrik: ”Besvär från tidigare behandling”. Text: ”Här finns besvär du har
// berättat om från tiden före Ella. Uppgifterna visar din
// behandlingshistorik.” … Före Ella betyder inte automatiskt
// avslutat/ingen uppföljning." The title is also an entry's status line,
// so no line says the symptom is over or not followed.
export const BEFORE_ELLA_SYMPTOMS_TITLE: Copy = {
  sv: "Besvär från tidigare behandling",
  en: "Symptoms from earlier treatment",
};
export const BEFORE_ELLA_SYMPTOMS_EXPLANATION: Copy = {
  sv: "Här finns besvär du har berättat om från tiden före Ella. Uppgifterna visar din behandlingshistorik.",
  en: "Here are the symptoms you have told us about from the time before Ella. The information shows your treatment history.",
};
/**
 * Before a start date she gave as approximate (startedOnApprox). Ö-849
 * (Emelie 2026-10-22): "Uppskattat datum visas med \"cirka\" före datumet."
 */
export const SYMPTOM_STARTED_APPROX_LABEL: Copy = {
  sv: "cirka",
  en: "about",
};

// ---------------------------------------------------------------------------
// The per-symptom fields of the check-ins (TES-1251, Ella 2.1 slice E).
//
// From F-04 v7 and F-05 v7 the check-ins ask the questions above once per
// symptom she chose, one field per (question, symptom), shown only for that
// symptom: `<form prefix>_symptom_<code>_<part>`, e.g.
// `week_symptom_nausea_severity`. The keys are new, never an older field's
// key reused: F-05's `month_symptom_severity_<code>` asked another question
// on another value set (mattlig/kraftig), and an answer must stay readable
// against the question it was given to.
//
// The readers that sort F-04 answers by key (f04-field-class, the journals)
// read these keys from here, so the forms and the readers cannot disagree.

export const CHECKIN_SYMPTOM_FIELD_PARTS = {
  severity: "SYMPTOM_SEVERITY",
  trend: "SYMPTOM_TREND",
  started: "SYMPTOM_STARTED_AT",
  intake: "SYMPTOM_INTAKE",
  note: "SYMPTOM_NOTE",
} as const;
export type CheckinSymptomFieldPart = keyof typeof CHECKIN_SYMPTOM_FIELD_PARTS;
export const CHECKIN_SYMPTOM_FIELD_PART_KEYS = Object.keys(
  CHECKIN_SYMPTOM_FIELD_PARTS,
) as CheckinSymptomFieldPart[];

/**
 * F-04's keys start with `week`, F-05's with `month`. TES-1441: F-02's
 * current-symptom details (intake v18) with `med`, the prefix of every F-02
 * key, so `med_symptom_nausea_severity`.
 */
export type CheckinFieldPrefix = "week" | "month" | "med";

export function checkinSymptomFieldKey(
  prefix: CheckinFieldPrefix,
  code: SymptomCode,
  part: CheckinSymptomFieldPart,
): string {
  return `${prefix}_symptom_${code}_${part}`;
}

/** Every per-symptom key of one check-in, symptom by symptom, in list order. */
export function checkinSymptomFieldKeys(prefix: CheckinFieldPrefix): string[] {
  return SYMPTOM_CODES.flatMap((code) =>
    CHECKIN_SYMPTOM_FIELD_PART_KEYS.map((part) =>
      checkinSymptomFieldKey(prefix, code, part),
    ),
  );
}

// ---------------------------------------------------------------------------
// Where a report comes from.

// TES-1439 — f02, f07 and renewal are the forms TES-1437 bridges
// (TES-1441, TES-1442, TES-1443). All three are written now: intake v18
// writes `f02` (TES-1441), her current symptoms as `current` reports; F-07
// v14 writes `f07` (TES-1442); the renewal check-in writes `renewal`
// (TES-1443).
export const SYMPTOM_SOURCE_SURFACES = [
  "f02",
  "f04",
  "f05",
  "f07",
  "renewal",
  "dose",
  "besvarsguide",
  "health",
] as const;
export type SymptomSourceSurface = (typeof SYMPTOM_SOURCE_SURFACES)[number];

// ---------------------------------------------------------------------------
// TES-1439 (S2 of TES-1437) — the period a report is about.
//
// Emelie 2026-10-19 (TES-1437): "Historiska/avslutade besvär, aktuell
// intensitet och månadsperiodens sammanfattning hålls isär. Äldre fritext
// eller sammanslagna svar får inte gissningsvis omvandlas till nya
// grafvärden." Drive "Kanoniskt datakontrakt 2026-10-19": "Grafer blandar
// inte aktuell intensitet, periodmaximum och historisk förekomst i en enda
// serie."
//
//   current      her answer about now ("Hur mycket besvär har du av detta
//                just nu?"): F-04, F-05, direct registration and every
//                follow-up. The only period a severity series is drawn from.
//   before_ella  a symptom she had in her earlier treatment (F-07, Ö-786
//                assumption (a)): which symptom, when it began and her note.
//                No severity, trend or intake, no safety outcome of its own;
//                listed as history with its source, never as today's value.
//
// A period summary ("sedan förra månadsavstämningen") is not a SymptomUpdate:
// F-05 v7 asks the register's "just nu" questions, and older aggregated
// answers stay under "Äldre registreringar" (Ö-446). A form that asks for a
// period maximum gets its own period here, never `current`.
//
// The question ids need no column: each answer column IS one register
// question (severity = SYMPTOM_SEVERITY, trend = SYMPTOM_TREND, startedOn =
// SYMPTOM_STARTED_AT, intake = SYMPTOM_INTAKE, note = SYMPTOM_NOTE), the
// bridges take only a field bound to exactly that id, and templateKey,
// templateVersion and sourceId lead back to the raw form answer.
export const SYMPTOM_REFERENCE_PERIODS = ["current", "before_ella"] as const;
export type SymptomReferencePeriod = (typeof SYMPTOM_REFERENCE_PERIODS)[number];

/**
 * The updates a severity series or "Senast" may read: her current reports.
 * Never "Borta", never a report about another period.
 */
export function isCurrentSymptomReport(
  u: Pick<SymptomUpdateView, "kind" | "referencePeriod">,
): boolean {
  return u.kind !== "resolved" && u.referencePeriod === "current";
}

// Registration outside a check-in. Emelie, Ö-447:
// "Direktregistrering av besvär utanför en incheckning är inte en bevakad vårdkanal. Akuta eller kraftiga signaler ska ändå utlösa säkerhetscopy och korrekt akutväg."
// So these are saved to her own history (`private_history`), never create a
// care-team case, and still run the safety evaluation below.
//
// TES-1438 — "dose" is no longer one of them (Emelie 2026-10-19, TES-1437:
// "Dosregistreringen ska inte ha egen besvärsregistrering eller
// länk/påminnelse till sådan."). It stays in SYMPTOM_SOURCE_SURFACES so the
// reports QA builds already saved from a dose keep reading as they were.
export const DIRECT_SYMPTOM_SURFACES = ["besvarsguide", "health"] as const;
export type DirectSymptomSurface = (typeof DIRECT_SYMPTOM_SURFACES)[number];

export type SymptomReviewRoute = "private_history" | "submitted_for_review";

// ---------------------------------------------------------------------------
// Safety questions. Emelie, Ö-448:
// "Bygg den gemensamma besvärsstrukturen i QA med de dokumenterade säkerhetsfrågorna som konfigurerbara arbetsantaganden. Kirill granskar dom besvärsspecifika frågorna före produktion."
//
// Two layers, versioned together as SYMPTOM_SAFETY_VERSION:
//
// 1. The acute screen, asked for EVERY symptom and always on. It is F-04's
//    `week_urgent_screen` (weekly-checkin-content.v4), question and options
//    verbatim: the safety question already in production, reused rather than
//    reworded (Drive §5.4: "Samma säkerhetsfråga och svarsskala ska återanvändas varhelst den förekommer.").
//    Any signal but "Inget av dessa" is an urgent hard stop, as in F-04.
//
// 2. Symptom-specific questions: WORKING ASSUMPTIONS awaiting Kirill, served
//    and evaluated only where the environment enables them
//    (SYMPTOM_SPECIFIC_SAFETY_ENABLED: QA on, prod off). v1 holds one, also
//    documented in F-04: `week_symptom_impact` ("Kan du äta och dricka?"),
//    whose "Har svårt att behålla mat/vätska" is F-04's hard stop
//    `week_cannot_keep_intake`, asked for the digestive symptoms where
//    keeping food and fluids down is the question.
//
// Plus two non-urgent signals that earn the safety copy without the acute
// path: severity "Kraftigt" and intake "Ofta svårt" ("kraftiga signaler").

export const SYMPTOM_SAFETY_VERSION = "symptom-safety.v1";

export interface SymptomSafetyQuestion {
  id: string;
  kind: "single_select" | "multi_select";
  label: Copy;
  options: (Option & { exclusive?: boolean })[];
  /** Answer values that are an urgent hard stop. */
  urgentValues: readonly string[];
}

export const SYMPTOM_ACUTE_SCREEN: SymptomSafetyQuestion = {
  id: "SYMPTOM_ACUTE_SCREEN",
  kind: "multi_select",
  label: {
    sv: "Har du något av följande just nu?",
    en: "Do you have any of the following right now?",
  },
  options: [
    {
      value: "kraftig_buksmarta",
      label: {
        sv: "Kraftig eller ihållande buksmärta",
        en: "Severe or persistent abdominal pain",
      },
    },
    {
      value: "kan_inte_behalla",
      label: {
        sv: "Kunnat behålla varken mat eller vätska i mer än ett dygn",
        en: "Unable to keep food or fluids down for over a day",
      },
    },
    { value: "svimning", label: { sv: "Svimmat", en: "Fainted" } },
    {
      value: "blod",
      label: {
        sv: "Blod i kräkning eller avföring",
        en: "Blood in vomit or stool",
      },
    },
    {
      value: "inget_av_dessa",
      label: { sv: "Inget av dessa", en: "None of these" },
      exclusive: true,
    },
  ],
  urgentValues: ["kraftig_buksmarta", "kan_inte_behalla", "svimning", "blod"],
};

const KEEP_FOOD_DOWN: SymptomSafetyQuestion = {
  id: "SYMPTOM_KEEP_FOOD_DOWN",
  kind: "single_select",
  label: { sv: "Kan du äta och dricka?", en: "Can you eat and drink?" },
  options: [
    {
      value: "ja_som_vanligt",
      label: { sv: "Ja, som vanligt", en: "Yes, as usual" },
    },
    {
      value: "mindre_an_vanligt",
      label: { sv: "Mindre än vanligt", en: "Less than usual" },
    },
    {
      value: "svart_att_behalla",
      label: {
        sv: "Har svårt att behålla mat/vätska",
        en: "Hard to keep food/fluids down",
      },
    },
  ],
  urgentValues: ["svart_att_behalla"],
};

/** Working assumptions (Ö-448), awaiting Kirill: symptom → its questions. */
export const SYMPTOM_SPECIFIC_SAFETY_V1: Readonly<
  Partial<Record<SymptomCode, readonly SymptomSafetyQuestion[]>>
> = {
  nausea: [KEEP_FOOD_DOWN],
  vomiting: [KEEP_FOOD_DOWN],
  diarrhea: [KEEP_FOOD_DOWN],
  intake_difficulty: [KEEP_FOOD_DOWN],
};

export interface SymptomSafetyOptions {
  /** SYMPTOM_SPECIFIC_SAFETY_ENABLED in the environment. */
  symptomSpecific: boolean;
}

/** The safety questions to ask about this symptom, in order. */
export function safetyQuestionsFor(
  code: SymptomCode,
  opts: SymptomSafetyOptions,
): SymptomSafetyQuestion[] {
  return [
    SYMPTOM_ACUTE_SCREEN,
    ...(opts.symptomSpecific ? (SYMPTOM_SPECIFIC_SAFETY_V1[code] ?? []) : []),
  ];
}

export type SymptomSafetyAnswers = Record<string, string | string[]>;

/**
 * Null when every question for the symptom has exactly one valid answer and
 * nothing else was sent; otherwise the reason. Every safety question is
 * required: a registration that skipped the screen is not one we can say
 * was screened.
 */
export function safetyAnswersError(
  code: SymptomCode,
  answers: SymptomSafetyAnswers,
  opts: SymptomSafetyOptions,
): string | null {
  const questions = safetyQuestionsFor(code, opts);
  const asked = new Set(questions.map((q) => q.id));
  for (const key of Object.keys(answers)) {
    if (!asked.has(key)) return `unknown_question:${key}`;
  }
  for (const q of questions) {
    const answer = answers[q.id];
    const values = q.options.map((o) => o.value);
    if (q.kind === "single_select") {
      if (typeof answer !== "string" || !values.includes(answer)) {
        return `invalid_answer:${q.id}`;
      }
      continue;
    }
    if (
      !Array.isArray(answer) ||
      answer.length === 0 ||
      new Set(answer).size !== answer.length ||
      answer.some((v) => !values.includes(v))
    ) {
      return `invalid_answer:${q.id}`;
    }
    const exclusive = q.options.filter((o) => o.exclusive).map((o) => o.value);
    if (answer.length > 1 && answer.some((v) => exclusive.includes(v))) {
      return `invalid_answer:${q.id}`;
    }
  }
  return null;
}

export type SymptomSafetyLevel = "none" | "severe" | "acute";

export interface SymptomSafetyResult {
  /** acute: the urgent path (112/1177) · severe: the safety copy · none. */
  level: SymptomSafetyLevel;
  /** Every rule raised, urgent and not, in a stable order. */
  rules: string[];
  /**
   * The urgent ones as hard stops, in the forms' shape, so the app's
   * existing acute view (acuteSafetyVariantFor) renders them unchanged.
   */
  hardStops: HardStop[];
}

/** Evaluates one report. Answers are assumed valid (safetyAnswersError). */
export function evaluateSymptomSafety(
  input: {
    symptomCode: SymptomCode;
    severity: SymptomSeverity;
    intake?: SymptomIntake | null;
    safetyAnswers: SymptomSafetyAnswers;
  },
  opts: SymptomSafetyOptions,
): SymptomSafetyResult {
  const hardStops: HardStop[] = [];
  for (const q of safetyQuestionsFor(input.symptomCode, opts)) {
    const answer = input.safetyAnswers[q.id];
    const chosen = Array.isArray(answer) ? answer : answer ? [answer] : [];
    for (const value of q.urgentValues) {
      if (!chosen.includes(value)) continue;
      hardStops.push({
        rule: `symptom_urgent_${value}`,
        urgent: true,
        patientMessageKey: "forms.safety.urgent",
        fieldKeys: [q.id],
      });
    }
  }
  const severe: string[] = [];
  if (input.severity === "kraftigt") severe.push("symptom_severe");
  if (input.intake === "ofta_svart") severe.push("symptom_intake_often_hard");
  const urgent = hardStops.map((h) => h.rule);
  return {
    level: urgent.length > 0 ? "acute" : severe.length > 0 ? "severe" : "none",
    rules: [...urgent, ...severe],
    hardStops,
  };
}

// ---------------------------------------------------------------------------
// The patient API (patient-api/v1/symptoms).

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const symptomReportBase = z.object({
  // TES-1444 — a stable key per registration from the app: a retried request
  // returns the result of the one it already saved instead of a second update
  // (Drive: "dubbelinskick får inte skapa dubbletter"). Same shape as
  // Dagbok's clientKey. Optional: builds up to 2.0.11 send none and are
  // served as before.
  clientKey: z.string().min(8).max(200).optional(),
  severity: z.enum(SYMPTOM_SEVERITY_VALUES),
  trend: z.enum(SYMPTOM_TREND_VALUES).optional(),
  intake: z.enum(SYMPTOM_INTAKE_VALUES).optional(),
  startedOn: isoDate.optional(),
  startedOnApprox: z.boolean().optional(),
  note: z.string().trim().min(1).max(2000).optional(),
  safetyAnswers: z.record(z.union([z.string(), z.array(z.string())])),
  sourceSurface: z.enum(DIRECT_SYMPTOM_SURFACES),
});

function checkReport(
  r: Pick<z.infer<typeof symptomReportBase>, "startedOn" | "startedOnApprox">,
  ctx: z.RefinementCtx,
): void {
  if (r.startedOnApprox !== undefined && r.startedOn === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "startedOnApprox needs startedOn",
      path: ["startedOnApprox"],
    });
  }
}

// TES-1452 (Emelie 2026-10-19, Ö-733): "En kort beskrivning är obligatorisk
// endast när patienten väljer Annat besvär. För namngivna besvär är
// kompletterande SYMPTOM_NOTE frivillig." The check-ins already require it
// (checkin-symptom-fields.ts); this is the same rule for registering one
// directly. Only when she chooses the symptom: "Hur är det nu?" about an
// entry she already described asks for no new description.
export const SYMPTOM_NOTE_REQUIRED_ISSUE = "note_required";

/** True when choosing this symptom requires her description (Ö-733). */
export function symptomNoteRequired(code: SymptomCode): boolean {
  return code === "other";
}

// A report where she chooses the symptom: the start-date rule, and her
// description when the symptom is "Annat besvär" (Ö-733).
function checkChosenSymptomReport(
  r: Pick<
    z.infer<typeof symptomReportBase>,
    "startedOn" | "startedOnApprox" | "note"
  > & { symptomCode: SymptomCode },
  ctx: z.RefinementCtx,
): void {
  checkReport(r, ctx);
  if (symptomNoteRequired(r.symptomCode) && r.note === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: SYMPTOM_NOTE_REQUIRED_ISSUE,
      path: ["note"],
    });
  }
}

/** POST /symptoms — register a symptom; appends to her open entry if any. */
export const registerSymptomSchema = symptomReportBase
  .extend({ symptomCode: z.enum(SYMPTOM_CODES) })
  .superRefine(checkChosenSymptomReport);
export type RegisterSymptomRequest = z.infer<typeof registerSymptomSchema>;

// TES-1443 (S6 of TES-1437) — the renewal check-in (CAR-18,
// POST cycles/:id/renew). Emelie 2026-10-19 (TES-1437): "Om den finns ska
// även den anslutas till samma strukturerade modell, med bevarad originaltext
// och kontext; skapa ingen ny separat enkät." Ö-787 (a): her free text to
// "Har du upplevt några biverkningar?" stays as she wrote it, and after it
// she may choose symptoms from the register and answer, per symptom, the
// same questions and safety screen as a direct registration under "Din
// hälsa". Each is a current report on her SymptomEntry, sent to the doctor
// with the renewal (`submitted_for_review`, sourceSurface "renewal").
//
// The report a direct registration sends, minus what the renewal supplies:
// the surface is "renewal", and the renewal's own one-per-cycle submission
// makes a retry a 409, so there is no clientKey.
export const renewalSymptomReportSchema = symptomReportBase
  .omit({ clientKey: true, sourceSurface: true })
  .extend({ symptomCode: z.enum(SYMPTOM_CODES) })
  .superRefine(checkChosenSymptomReport);
export type RenewalSymptomReport = z.infer<typeof renewalSymptomReportSchema>;

/** Her chosen symptoms in one renewal: each at most once, none required. */
export const renewalSymptomsSchema = z
  .array(renewalSymptomReportSchema)
  .max(SYMPTOM_CODES.length)
  .superRefine((reports, ctx) => {
    const codes = reports.map((r) => r.symptomCode);
    if (new Set(codes).size !== codes.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "duplicate_symptom",
      });
    }
  });

/** POST /symptoms/:entryId/updates — "Hur är det nu?" about one entry. */
export const addSymptomUpdateSchema =
  symptomReportBase.superRefine(checkReport);
export type AddSymptomUpdateRequest = z.infer<typeof addSymptomUpdateSchema>;

export interface SymptomUpdateView {
  id: string;
  kind: "report" | "reopened" | "resolved";
  severity: SymptomSeverity | null;
  trend: SymptomTrend | null;
  intake: SymptomIntake | null;
  startedOn: string | null;
  startedOnApprox: boolean | null;
  note: string | null;
  observedAt: string;
  sourceSurface: SymptomSourceSurface;
  reviewRoute: SymptomReviewRoute;
  safetyLevel: SymptomSafetyLevel;
  /**
   * TES-1439 — the period the report is about. Only `current` reports carry
   * a severity and belong to the series (isCurrentSymptomReport).
   */
  referencePeriod: SymptomReferencePeriod;
}

/**
 * open: she follows it, and every check-in asks about it (Ö-724) ·
 * resolved: her "Borta" · history (TES-1439): known only from a report
 * about her earlier treatment; never followed up, listed apart, and opened
 * by her first current report of the symptom. A client below
 * CLIENT_CONTRACT_CHANGES.symptomReferencePeriod is never served a history
 * entry.
 */
export type SymptomEntryStatus = "open" | "resolved" | "history";

export interface SymptomEntryView {
  id: string;
  symptomCode: string;
  label: Copy | null;
  registerVersion: number;
  status: SymptomEntryStatus;
  createdFrom: SymptomSourceSurface;
  firstReportedAt: string;
  resolvedAt: string | null;
  /**
   * Her latest CURRENT report (not "Borta", not one about before Ella): what
   * a follow-up pre-fills from. Null for a history entry.
   */
  latestReport: SymptomUpdateView | null;
  /** Oldest first. Only on the single-entry read. */
  updates?: SymptomUpdateView[];
}

export interface SymptomRegistrationResult {
  entry: SymptomEntryView;
  /** True when the report was appended to an entry she already had. */
  appended: boolean;
  /** Always private_history on this API (Ö-447). */
  reviewRoute: SymptomReviewRoute;
  safety: SymptomSafetyResult;
}

export interface SymptomQuestionsView {
  registerVersion: number;
  symptoms: { code: SymptomCode; label: Copy }[];
  questions: typeof SYMPTOM_QUESTIONS;
  resolvedLabel: Copy;
  safetyVersion: string;
  /** Per symptom, the safety questions this environment asks, in order. */
  safetyQuestions: Record<SymptomCode, SymptomSafetyQuestion[]>;
}

// ---------------------------------------------------------------------------
// The doctor's read (employee-api/v1/patients/:patientId/symptoms).
//
// Doctor only: symptom details are medical data the coach never sees (F-04
// document: the coach sees only "Medicinskt svar hanteras av läkare"), the
// same grant as the dose log (Ö-423). Every update says whether it was
// submitted for review or only saved by her (`reviewRoute`): a registration
// outside a check-in is not a monitored channel (Ö-447), and the view must
// not suggest that someone was expected to act on it.

export interface StaffSymptomUpdateView extends SymptomUpdateView {
  /** Her safety answers, keyed by question id; null for "Borta". */
  safetyAnswers: SymptomSafetyAnswers | null;
  safetyVersion: string | null;
  safetyRules: string[];
  doseEventId: string | null;
}

export interface StaffSymptomEntryView extends Omit<
  SymptomEntryView,
  "updates"
> {
  /** Oldest first. */
  updates: StaffSymptomUpdateView[];
}

export interface StaffPatientSymptomsView {
  /**
   * Open entries first, then resolved, then history (TES-1439); newest
   * first within each.
   */
  entries: StaffSymptomEntryView[];
}

/**
 * TES-1252 — the doctor sees what she registered outside a check-in, saved
 * to her own history (`private_history`) and never submitted. Ö-675,
 * answered by Emelie 2026-10-19 (TES-1440): "Läkaren ska kunna se
 * direktregistrerade besvär från Din hälsa även innan de skickats in genom
 * en incheckning. De visas som bakgrund i patientens besvärshistorik med
 * märkningen ”Registrerat av patienten – inte inskickat för granskning”."
 * Saving one creates no review task. Set to false and the staff read drops
 * every private report server side, and with it every entry that has
 * nothing else, so no admin build can show them.
 */
export const DOCTOR_SEES_PRIVATE_SYMPTOM_HISTORY = true;

// ---------------------------------------------------------------------------
// Her older-format symptom answers (GET patient-api/v1/symptoms/older-registrations).
//
// Emelie, Ö-446 (TES-1396): "Äldre samlade svar får inte skrivas om till den
// nya modellen där varje besvär registreras separat … Om äldre svar visas ska
// dom ligga separat under exempelvis: Äldre registreringar och märkas: Äldre
// format. Dom får inte blandas in i den nya jämförbara kurvan för enskilda
// besvär."
//
// So these are read straight from her F-04/F-05 FormSubmissions, nothing is
// copied into SymptomEntry, and the shape shares nothing with
// SymptomEntryView: an older answer has no symptom code, severity or trend a
// per-symptom series could pick up. Each item is the question as the form
// version she answered worded it and her answer with that version's labels
// (Drive §11: "Historiska svar behåller den formulärversion patienten såg").

/** Patient-facing copy of the section, Ö-446 verbatim. */
export const OLDER_SYMPTOM_REGISTRATIONS_TITLE: Copy = {
  sv: "Äldre registreringar",
  en: "Older registrations",
};
export const OLDER_SYMPTOM_FORMAT_LABEL: Copy = {
  sv: "Äldre format",
  en: "Older format",
};
/**
 * TES-1440 — the sentence under the heading. Ö-700, Emelie 2026-10-19
 * (Drive 2.1 product definition), verbatim; the English is ours.
 */
export const OLDER_SYMPTOM_REGISTRATIONS_EXPLANATION: Copy = {
  sv: "De här besvären registrerades med tidigare frågor och visas därför separat från dina besvärskurvor.",
  en: "These symptoms were registered with earlier questions, so they are shown separately from your symptom curves.",
};

export interface OlderSymptomAnswerItem {
  /** The form field it answered; stable within its template version. */
  fieldKey: string;
  question: Copy;
  /** Option labels joined with ", ", or her own text (same in sv and en). */
  answer: Copy;
}

export interface OlderSymptomRegistration {
  /** The FormSubmission it is read from. */
  submissionId: string;
  /** weekly_checkin (F-04) or milestone (F-05). */
  source: string;
  /** The form's own title in the version she answered. */
  sourceTitle: Copy;
  templateVersion: number;
  /** ISO-8601. */
  submittedAt: string;
  /** Always "older": the answer used an older question format. */
  format: "older";
  /** Template order. Never empty. */
  items: OlderSymptomAnswerItem[];
}

export interface OlderSymptomRegistrationsView {
  title: Copy;
  formatLabel: Copy;
  /** Ö-700's sentence under the heading. */
  explanation: Copy;
  /** Newest first. Empty when she never reported a symptom in F-04/F-05. */
  registrations: OlderSymptomRegistration[];
}
